import {
	OffersWarrantyType,
	ConstructionSiteSubmissionPayload,
	DecennialCertificatePayload,
	GetConstructionSiteDerogationsResponse,
	ParticipantToConstructionSiteReception,
	ParticipantToConstructionSiteSubmission,
	ReceptionStep1Payload,
	ReferenceableFile,
	SubmissionWithBinderWarrantiesSelected,
	SubmissionWithOperationDetailsPayload,
	SubmissionWithParticipantsAndValidDecennialCertificate,
	UpdateConstructionSiteDerogationBody,
	OfferPricingOutput,
	SuccessResponse,
	UpdateConstructionSiteReceptionCostBody,
	UpdateConstructionSiteReceptionCostResponse,
} from "@dune/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { privateAxios } from "../axios";
import { constructionSiteSubmissionQueryKeys } from "../query-keys/constructionSiteSubmission.query-keys";

const getConstructionSiteSubmission = async (constructionSiteId: number) => {
	const { data } =
		await privateAxios.get<ConstructionSiteSubmissionPayload | null>(
			`/construction-sites-submission/${constructionSiteId}`,
		);
	return data;
};

export const useGetConstructionSiteSubmission = (
	constructionSiteId: number,
) => {
	return useQuery({
		queryKey: constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
		queryFn: () => getConstructionSiteSubmission(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

export const useUpdateConstructionSiteSubmissionDocuments = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (fileIds: string[]) => {
			const { data } = await privateAxios.post(
				`/construction-sites-submission/${constructionSiteId}/documents`,
				{ fileIds },
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			});
		},
		onError: (error) => {
			console.error(error);
			toast.error(
				"Une erreur est survenue lors de l'enregistrement des documents",
			);
		},
	});
};

const getConstructionSiteSubmissionOperation = async (
	constructionSiteId: number,
) => {
	const { data } =
		await privateAxios.get<SubmissionWithOperationDetailsPayload>(
			`/construction-sites-submission/${constructionSiteId}/operation`,
		);
	return data;
};

export const useGetConstructionSiteSubmissionOperation = (
	constructionSiteId: number,
) => {
	return useQuery({
		queryKey: constructionSiteSubmissionQueryKeys.operation(constructionSiteId),
		queryFn: () => getConstructionSiteSubmissionOperation(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

const getConstructionSiteSubmissionParticipants = async (
	constructionSiteId: number,
) => {
	const { data } =
		await privateAxios.get<SubmissionWithParticipantsAndValidDecennialCertificate>(
			`/construction-sites-submission/${constructionSiteId}/participants`,
		);
	return data;
};

export const useGetConstructionSiteSubmissionParticipants = (
	constructionSiteId: number,
) => {
	return useQuery({
		queryKey:
			constructionSiteSubmissionQueryKeys.participants(constructionSiteId),
		queryFn: () =>
			getConstructionSiteSubmissionParticipants(constructionSiteId),
		enabled: !!constructionSiteId,
		retry: false,
	});
};

const getReferenceableFiles = async (constructionSiteId: number) => {
	const { data } = await privateAxios.get<ReferenceableFile[]>(
		`/construction-sites-submission/${constructionSiteId}/referenceable-files`,
	);
	return data;
};

export const useGetReferenceableFiles = (constructionSiteId: number) => {
	return useQuery({
		queryKey:
			constructionSiteSubmissionQueryKeys.referenceableFiles(
				constructionSiteId,
			),
		queryFn: () => getReferenceableFiles(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

const getConstructionSiteReception = async (
	constructionSiteId: number,
): Promise<ReceptionStep1Payload> => {
	const { data } = await privateAxios.get<ReceptionStep1Payload>(
		`/construction-sites-reception/${constructionSiteId}`,
	);
	return data;
};

export const useGetConstructionSiteReception = (constructionSiteId: number) => {
	return useQuery({
		queryKey: [
			...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			"reception",
		],
		queryFn: () => getConstructionSiteReception(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

const getConstructionSiteReceptionParticipants = async (
	constructionSiteId: number,
): Promise<ParticipantToConstructionSiteReception[]> => {
	const { data } = await privateAxios.get<{
		participantToConstructionSiteReceptions: ParticipantToConstructionSiteReception[];
	}>(`/construction-sites-reception/${constructionSiteId}/participants`);
	return data.participantToConstructionSiteReceptions;
};

export const useGetConstructionSiteReceptionParticipants = (
	constructionSiteId: number,
) => {
	return useQuery({
		queryKey:
			constructionSiteSubmissionQueryKeys.participantsReception(
				constructionSiteId,
			),
		queryFn: () => getConstructionSiteReceptionParticipants(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

export const getConstructionSiteDerogations = async (
	constructionSiteId: number,
): Promise<GetConstructionSiteDerogationsResponse> => {
	const { data } =
		await privateAxios.get<GetConstructionSiteDerogationsResponse>(
			`/construction-sites-submission/${constructionSiteId}/derogations`,
		);
	return data;
};

export const useGetConstructionSiteDerogations = (
	constructionSiteId: number,
) => {
	return useQuery({
		queryKey: [
			...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			"derogations",
		],
		queryFn: () => getConstructionSiteDerogations(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

export const updateConstructionSiteDerogations = async (
	constructionSiteId: number,
	payload: { derogations: UpdateConstructionSiteDerogationBody[] },
) => {
	const { data } = await privateAxios.patch(
		`/construction-sites-submission/${constructionSiteId}/derogations`,
		payload,
	);
	return data;
};

export const useUpdateConstructionSiteDerogations = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: {
			derogations: UpdateConstructionSiteDerogationBody[];
		}) => updateConstructionSiteDerogations(constructionSiteId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
					"derogations",
				],
			});
		},
	});
};

export const getBinderWarrantiesTypesSelected = async (
	constructionSiteId: number,
) => {
	const { data } = await privateAxios.get<
		SubmissionWithBinderWarrantiesSelected["selectedBinderWarranties"][number]["binderWarranty"][]
	>(
		`/construction-sites-submission/${constructionSiteId}/binder-warranties-selected`,
	);
	return data;
};

export const useBinderWarrantiesTypesSelected = (
	constructionSiteId: number,
) => {
	return useQuery({
		queryKey:
			constructionSiteSubmissionQueryKeys.binderWarrantiesTypesSelected(
				constructionSiteId,
			),
		queryFn: () => getBinderWarrantiesTypesSelected(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

export const getParticipantToConstructionSiteSubmission = async (
	constructionSiteId: number,
	participantId: number,
) => {
	const { data } = await privateAxios.get<
		ParticipantToConstructionSiteSubmission & {
			decennialCertificatesOfSubmissionOrValidFromOthers: DecennialCertificatePayload[];
		}
	>(
		`/construction-sites-submission/${constructionSiteId}/participants/${participantId}`,
	);
	return data;
};

export const useGetParticipantToConstructionSiteSubmission = (
	constructionSiteId: number,
	participantId: number,
) => {
	return useQuery({
		queryKey: constructionSiteSubmissionQueryKeys.participant(
			constructionSiteId,
			participantId,
		),
		queryFn: () =>
			getParticipantToConstructionSiteSubmission(
				constructionSiteId,
				participantId,
			),
		enabled: !!constructionSiteId && !!participantId,
	});
};

export const updateConstructionSiteOfferPricing = async (
	constructionSiteId: number,
	payload: Partial<OfferPricingOutput<OffersWarrantyType>>,
) => {
	const { data } = await privateAxios.patch<SuccessResponse>(
		`/construction-sites-submission/${constructionSiteId}/offer-pricing`,
		payload,
	);
	return data;
};

export const useUpdateConstructionSiteOfferPricing = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (
			payload: Partial<
				OfferPricingOutput<OffersWarrantyType> & {
					constructionSiteOfferId: number;
				}
			>,
		) => updateConstructionSiteOfferPricing(constructionSiteId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					...constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
					"offer-pricing",
				],
			});
		},
	});
};

const updateConstructionSiteReceptionCost = async (
	constructionSiteId: number,
	payload: UpdateConstructionSiteReceptionCostBody,
): Promise<UpdateConstructionSiteReceptionCostResponse> => {
	const { data } = await privateAxios.patch(
		`/construction-sites-reception/${constructionSiteId}/reception-cost`,
		payload,
	);
	return data;
};

export const useUpdateConstructionSiteReceptionCost = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: UpdateConstructionSiteReceptionCostBody) =>
			updateConstructionSiteReceptionCost(constructionSiteId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSiteSubmissionQueryKeys.detail(constructionSiteId),
			});
		},
	});
};
